// ============================================================================
// stage1-video-policy.js — single source of truth for Stage 1 video size caps
// ----------------------------------------------------------------------------
// DEC-COUNCIL-001 P1a.5 (2026-05-12) — Naruto's user-visible Customer Zero
// bundle. Before this reconciliation, three files disagreed:
//
//   src/services/stage1-video-policy.js:5  IMPORT_HARD = 800 MB
//   src/pages/stages/Stage1DataEntry.jsx:3017  MAX_VIDEO_MB = 2048 (2 GB)
//   src/pages/stages/stage1/FileImportView.jsx:33  MAX_VIDEO_SIZE = 5 GB
//
// Rodney's UX today: file-picker copy said "up to 5 GB", Stage 1 guard
// rejected 2 GB+, processing-mode rejected 800 MB+. The video silently
// vanished into a different rejection at every layer. Verified by Tier 1
// ground-truth survey + DEC-COUNCIL-001.
//
// This module exports VIDEO_CAPS — the canonical caps both Stage1DataEntry
// and FileImportView import. The production cloud-proxy route is buffered and
// admits up to 512 MiB with bounded concurrency. Videos above that ceiling remain useful
// local/reference-only evidence up to the 2 GiB local import cap, but must not
// be sent to /api/media/transcode-and-store until streamed ingestion exists.
//
// Backward-compatibility: legacy STAGE1_VIDEO_LIMITS export retained as
// aliases so existing call sites keep working until the consumers migrate
// to VIDEO_CAPS.
// ============================================================================

// Callers pass either a raw byte count or a File-like object exposing `.size`.
// The body normalises both via `typeof === 'number'` + `Number(x?.size)`, so
// null/undefined are tolerated (they fall through to 0).
type FileOrSize = number | { size?: number | null } | null | undefined;

const MB = 1024 * 1024;
const GB = 1024 * MB;

// Tier thresholds within the import-accepted range.
const CLOUD_PROXY_SOURCE_CAP = 512 * MB;  // Must match backend trusted proxy cap
const INLINE_TRANSCRIBE_CAP = CLOUD_PROXY_SOURCE_CAP;
// Browser media elements may decode far more than the small poster they emit.
// Keep poster capture inside the same bounded seam as inline processing; an
// accepted source above 512 MiB remains a descriptor/reference only.
const BROWSER_POSTER_CAP    = CLOUD_PROXY_SOURCE_CAP;

// Local/reference imports stay bounded independently of cloud processing.
const HARD_CAP_PRE_WORKER = 2 * GB;

// Soft warning surfaces at the file-picker as "this is large, are you sure?"
const SOFT_WARNING_CAP = 1 * GB;

/**
 * Fixed local/reference import cap. The retired qm_use_job_workers browser
 * flag deliberately cannot raise this value.
 */
export function getActiveHardCap() {
    return HARD_CAP_PRE_WORKER;
}

/**
 * Canonical caps. Use these everywhere instead of bare numeric literals.
 * Consumers should call `getActiveHardCap()` for the cap value to enforce
 * (it returns the worker-aware number); the constants below are for display
 * and for tier-decision logic inside processing-mode.
 */
export const VIDEO_CAPS = Object.freeze({
    CLOUD_PROXY_SOURCE: CLOUD_PROXY_SOURCE_CAP,
    INLINE_TRANSCRIBE: INLINE_TRANSCRIBE_CAP,
    BROWSER_POSTER:    BROWSER_POSTER_CAP,
    SOFT_WARNING:      SOFT_WARNING_CAP,
    HARD_PRE_WORKER:   HARD_CAP_PRE_WORKER,
});

// ── Backward-compat alias (do not remove without grep across consumers) ────
// Earlier code (pre-P1a.5) imported STAGE1_VIDEO_LIMITS.IMPORT_HARD_CAP_BYTES
// and INLINE_PROCESSING_CAP_BYTES directly. We keep these aliases so the
// switchover lands cleanly even if a consumer is missed by the migration.
export const STAGE1_VIDEO_LIMITS = Object.freeze({
    INLINE_PROCESSING_CAP_BYTES: INLINE_TRANSCRIBE_CAP,
    IMPORT_HARD_CAP_BYTES: HARD_CAP_PRE_WORKER,
});

export function describeMegabytes(bytes: unknown): string {
    const mb = Math.max(0, Number(bytes) || 0) / MB;
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${Math.round(mb)} MB`;
}

/**
 * Returns the processing decision for a Stage 1 video of `size` bytes.
 *
 * Current production behaviour:
 *   - size > 2 GiB        → reject
 *   - size > 512 MiB      → local/reference-only evidence; no cloud request
 *   - size <= 512 MiB     → inline evidence + eligible trusted cloud proxy
 *
 * Reference-only videos are deliberately not decoded in the browser and are
 * not copied into IndexedDB. Their source descriptor/path remains available
 * for this session and later folder reconnection.
 */
export function getStage1VideoProcessingMode(fileOrSize: FileOrSize) {
    const size = typeof fileOrSize === 'number'
        ? fileOrSize
        : Number(fileOrSize?.size) || 0;
    const hardCap = getActiveHardCap();

    if (size > hardCap) {
        return {
            kind: 'reject',
            accepted: false,
            processInline: false,
            thumbnailOnly: false,
            capturePosterInBrowser: false,
            persistOriginalInIndexedDb: false,
            localReferenceOnly: false,
            cloudProxyEligible: false,
            cloudProxyReason: 'source_exceeds_local_reference_import_limit',
            statusMessage: `Not imported: it exceeds ${describeMegabytes(hardCap)}`,
        };
    }

    if (size > CLOUD_PROXY_SOURCE_CAP) {
        return {
            kind: 'reference_only',
            accepted: true,
            processInline: false,
            thumbnailOnly: false,
            capturePosterInBrowser: false,
            persistOriginalInIndexedDb: false,
            localReferenceOnly: true,
            cloudProxyEligible: false,
            cloudProxyReason: 'source_exceeds_512_mib_buffered_proxy_limit',
            statusMessage: 'Kept local/reference-only: over the 512 MiB cloud and browser limit. Reconnect the source folder after a reload.',
        };
    }
    return {
        kind: 'inline',
        accepted: true,
        processInline: true,
        thumbnailOnly: false,
        capturePosterInBrowser: true,
        persistOriginalInIndexedDb: true,
        localReferenceOnly: false,
        cloudProxyEligible: true,
        cloudProxyReason: null,
        statusMessage: 'Transcribed',
    };
}

/**
 * Structural guard for poster capture. Callers pass the expensive operation as
 * a callback so disallowed sources never create/load a browser media element.
 */
export async function captureStage1VideoPosterIfAllowed<T>(
    processingMode: { capturePosterInBrowser?: unknown } | null | undefined,
    capturePoster: () => Promise<T>,
): Promise<T | null> {
    if (processingMode?.capturePosterInBrowser !== true) return null;
    return capturePoster();
}

/**
 * Legacy compatibility helper for already-open sessions. The active policy no
 * longer emits the retired oversized-worker tier. It delegates to the same
 * truthful limits so an older caller cannot bypass the 512 MiB cloud or 2 GiB
 * local import caps.
 */
export function getSoftDegradeModeForWorkerFailure(fileOrSize: FileOrSize) {
    return { ...getStage1VideoProcessingMode(fileOrSize), degraded: true };
}
